import { PERMISSION_CODES } from './mockPermissions';

export type PermissionRequirement = ReadonlyArray<readonly string[]>;

export interface AuthorizedRedirectTarget {
  path: string;
  requirement: PermissionRequirement;
}

const requireAll = (...permissionCodes: string[]): PermissionRequirement => [
  permissionCodes,
];

const requireAnyClause = (
  ...permissionCodeClauses: readonly string[][]
): PermissionRequirement => permissionCodeClauses;

const combineRequirements = (
  left: PermissionRequirement,
  right: PermissionRequirement,
): PermissionRequirement =>
  left.flatMap((leftClause) =>
    right.map((rightClause) => [...new Set([...leftClause, ...rightClause])]));

export const INTEGRATION_PERMISSION_REQUIREMENTS = {
  'wechat-payment': requireAll(
    PERMISSION_CODES.wechatPaymentSettingsPage,
    PERMISSION_CODES.wechatPaymentSettingsQuery,
  ),
  'notification-channels': requireAll(
    PERMISSION_CODES.notificationChannelManagementPage,
    PERMISSION_CODES.notificationChannelRead,
  ),
  'wechat-app': requireAll(
    PERMISSION_CODES.wechatAppSettingsPage,
    PERMISSION_CODES.wechatAppSettingsQuery,
  ),
} as const;

const integrationRequirement: PermissionRequirement = Object.values(
  INTEGRATION_PERMISSION_REQUIREMENTS,
).flat();

export const ROUTE_PERMISSION_REQUIREMENTS: Readonly<
Record<string, PermissionRequirement>
> = {
  '/workbench': requireAll(PERMISSION_CODES.workbenchPage),
  '/member-center': requireAll(PERMISSION_CODES.memberCenterMenu),
  '/member-center/member-list': requireAll(PERMISSION_CODES.memberListPage),
  '/member-center/member-certification': requireAnyClause(
    [
      PERMISSION_CODES.memberCertificationPage,
      PERMISSION_CODES.memberCertificationView,
    ],
    [
      PERMISSION_CODES.memberCertificationPage,
      PERMISSION_CODES.memberCertificationReview,
    ],
    [
      PERMISSION_CODES.memberCertificationPage,
      PERMISSION_CODES.memberCertificationGrant,
    ],
    [
      PERMISSION_CODES.memberCertificationPage,
      PERMISSION_CODES.memberCertificationRevoke,
    ],
    [
      PERMISSION_CODES.memberCertificationPage,
      PERMISSION_CODES.memberCertificationConfig,
    ],
  ),
  '/member-center/tag-management': requireAll(
    PERMISSION_CODES.memberTagManagementPage,
    PERMISSION_CODES.memberTagRead,
  ),
  '/member-center/audience/plans/new': requireAll(
    PERMISSION_CODES.memberAudienceSegmentationPage,
    PERMISSION_CODES.memberAudienceRead,
  ),
  '/member-center/audience/plans/:id/edit': requireAll(
    PERMISSION_CODES.memberAudienceSegmentationPage,
    PERMISSION_CODES.memberAudienceRead,
  ),
  '/member-center/audience/plans/:id': requireAll(
    PERMISSION_CODES.memberAudienceSegmentationPage,
    PERMISSION_CODES.memberAudienceRead,
  ),
  '/member-center/audience/plans': requireAll(
    PERMISSION_CODES.memberAudienceSegmentationPage,
    PERMISSION_CODES.memberAudienceRead,
  ),
  '/member-center/audience/packages': requireAll(
    PERMISSION_CODES.memberAudienceSegmentationPage,
    PERMISSION_CODES.memberAudienceRead,
  ),
  '/member-center/audience/packages/:id': requireAll(
    PERMISSION_CODES.memberAudienceSegmentationPage,
    PERMISSION_CODES.memberAudienceRead,
  ),
  '/member-center/audience-segmentation/:id': requireAll(
    PERMISSION_CODES.memberAudienceSegmentationPage,
    PERMISSION_CODES.memberAudienceRead,
  ),
  '/member-center/audience-segmentation': requireAll(
    PERMISSION_CODES.memberAudienceSegmentationPage,
    PERMISSION_CODES.memberAudienceRead,
  ),
  '/member-center/member-relations': requireAll(
    PERMISSION_CODES.memberRelationsPage,
    PERMISSION_CODES.memberRelationsView,
  ),
  '/member-center/member-relations/:memberId': requireAll(
    PERMISSION_CODES.memberRelationsPage,
    PERMISSION_CODES.memberRelationsView,
  ),
  '/member-center/member-network': requireAll(
    PERMISSION_CODES.memberNetworkMenu,
    PERMISSION_CODES.memberNetworkPage,
    PERMISSION_CODES.memberNetworkView,
  ),
  '/member-center/member-benefits': requireAll(
    PERMISSION_CODES.memberBenefitsPage,
  ),
  '/member-center/semiannual-confirmation': requireAll(
    PERMISSION_CODES.memberSemiannualConfirmationPage,
    PERMISSION_CODES.memberConfirmationQuery,
  ),
  '/member-center/member-reports': requireAll(
    PERMISSION_CODES.memberReportsPage,
    PERMISSION_CODES.memberReportRead,
  ),
  '/activity-center': requireAll(PERMISSION_CODES.activityCenterMenu),
  '/activity-center/activity-list': requireAll(
    PERMISSION_CODES.activityListPage,
  ),
  '/activity-center/benchu-ai': requireAll(
    PERMISSION_CODES.activityAiPage,
    PERMISSION_CODES.activityAiRead,
  ),
  '/activity-center/frontend-operation': requireAll(
    PERMISSION_CODES.activityFrontendOperationPage,
    PERMISSION_CODES.activityFrontendOperationRead,
  ),
  '/activity-center/staff-authorization': requireAnyClause(
    [PERMISSION_CODES.activityListPage, PERMISSION_CODES.activityProcessManage],
    [PERMISSION_CODES.activityListPage, PERMISSION_CODES.labelAccessManage],
    [
      PERMISSION_CODES.activityListPage,
      PERMISSION_CODES.activityVerificationGrantManage,
    ],
  ),
  '/activity-center/activity-workflow': requireAll(
    PERMISSION_CODES.activityWorkflowPage,
  ),
  '/activity-center/process-templates': requireAnyClause(
    [PERMISSION_CODES.activityProcessTemplateView],
    [PERMISSION_CODES.activityProcessTemplateManage],
  ),
  '/activity-center/registration-management': requireAll(
    PERMISSION_CODES.registrationManagementPage,
  ),
  '/activity-center/check-in-management': requireAll(
    PERMISSION_CODES.checkInManagementPage,
  ),
  '/activity-center/seating-management': requireAll(
    PERMISSION_CODES.seatingManagementPage,
  ),
  '/activity-center/label-printing': requireAll(
    PERMISSION_CODES.labelPrintingPage,
  ),
  '/activity-center/label-templates': requireAll(
    PERMISSION_CODES.labelTemplatesPage,
    PERMISSION_CODES.labelPublicTemplateManage,
  ),
  '/activity-center/activity-notification': requireAll(
    PERMISSION_CODES.activityNotificationPage,
  ),
  '/activity-center/activity-lottery': requireAll(
    PERMISSION_CODES.activityLotteryPage,
    PERMISSION_CODES.activityLotteryGet,
  ),
  '/activity-center/activity-landing-page': requireAll(
    PERMISSION_CODES.activityLandingPage,
  ),
  '/activity-center/activity-summary': requireAll(
    PERMISSION_CODES.activitySummaryPage,
  ),
  '/finance-center': requireAll(PERMISSION_CODES.financeCenterMenu),
  '/finance-center/payment-management': requireAll(
    PERMISSION_CODES.paymentManagementPage,
  ),
  '/finance-center/refund-management': requireAll(
    PERMISSION_CODES.refundManagementPage,
  ),
  '/finance-center/sponsorship-management': requireAll(
    PERMISSION_CODES.sponsorshipManagementPage,
  ),
  '/finance-center/material-management': requireAll(
    PERMISSION_CODES.materialManagementPage,
  ),
  '/finance-center/invoice-management': requireAll(
    PERMISSION_CODES.invoiceManagementPage,
  ),
  '/finance-center/financial-approval': requireAll(
    PERMISSION_CODES.financialApprovalPage,
  ),
  '/finance-center/financial-reports': requireAll(
    PERMISSION_CODES.financialReportsPage,
  ),
  '/notification-center': requireAll(PERMISSION_CODES.notificationCenterMenu),
  '/notification-center/channel-management':
    INTEGRATION_PERMISSION_REQUIREMENTS['notification-channels'],
  '/notification-center/notification-templates': requireAll(
    PERMISSION_CODES.notificationTemplatesPage,
  ),
  '/notification-center/notification-send': requireAll(
    PERMISSION_CODES.notificationSendPage,
  ),
  '/notification-center/trigger-strategies': requireAll(
    PERMISSION_CODES.notificationTriggerStrategiesPage,
  ),
  '/notification-center/send-records': requireAll(
    PERMISSION_CODES.notificationSendRecordsPage,
  ),
  '/knowledge-base': requireAll(PERMISSION_CODES.knowledgeBaseMenu),
  '/knowledge-base/asset-management': requireAll(
    PERMISSION_CODES.knowledgeAssetManagementPage,
  ),
  '/knowledge-base/activity-materials': requireAll(
    PERMISSION_CODES.knowledgeActivityMaterialsPage,
  ),
  '/knowledge-base/template-library': requireAll(
    PERMISSION_CODES.knowledgeTemplateLibraryPage,
  ),
  '/knowledge-base/user-feedback': requireAll(
    PERMISSION_CODES.knowledgeBaseMenu,
    PERMISSION_CODES.knowledgeUserFeedbackPage,
    PERMISSION_CODES.userFeedbackRead,
  ),
  '/task-approval': requireAll(PERMISSION_CODES.taskApprovalMenu),
  '/task-approval/todo-center': requireAll(PERMISSION_CODES.taskTodoCenterPage),
  '/task-approval/approval-workflow': requireAll(
    PERMISSION_CODES.taskApprovalWorkflowPage,
  ),
  '/task-approval/exception-tasks': requireAll(
    PERMISSION_CODES.taskExceptionTasksPage,
  ),
  '/data-reports': requireAll(PERMISSION_CODES.dataReportsMenu),
  '/data-reports/operations-dashboard': requireAll(
    PERMISSION_CODES.operationsDashboardPage,
  ),
  '/data-reports/member-analysis': requireAll(
    PERMISSION_CODES.memberAnalysisPage,
  ),
  '/data-reports/activity-analysis': requireAll(
    PERMISSION_CODES.activityAnalysisPage,
  ),
  '/data-reports/registration-checkin-analysis': requireAll(
    PERMISSION_CODES.registrationCheckinAnalysisPage,
  ),
  '/data-reports/finance-analysis': requireAll(
    PERMISSION_CODES.financeAnalysisPage,
  ),
  '/data-reports/notification-analysis': requireAll(
    PERMISSION_CODES.notificationAnalysisPage,
  ),
  '/data-reports/lottery-analysis': requireAll(
    PERMISSION_CODES.lotteryAnalysisPage,
  ),
  '/system-settings': [
    [PERMISSION_CODES.systemSettingsMenu],
    ...integrationRequirement,
  ],
  '/system-settings/organization-settings': requireAll(
    PERMISSION_CODES.systemSettingsMenu,
    PERMISSION_CODES.organizationSettingsPage,
  ),
  '/system-settings/site-settings': requireAnyClause(
    [
      PERMISSION_CODES.systemSettingsMenu,
      PERMISSION_CODES.siteSettingsPage,
      PERMISSION_CODES.siteHelpRead,
    ],
    [
      PERMISSION_CODES.systemSettingsMenu,
      PERMISSION_CODES.siteSettingsPage,
      PERMISSION_CODES.siteAgreementRead,
    ],
    [
      PERMISSION_CODES.systemSettingsMenu,
      PERMISSION_CODES.siteSettingsPage,
      PERMISSION_CODES.siteRegistrationSettingsRead,
    ],
    [
      PERMISSION_CODES.systemSettingsMenu,
      PERMISSION_CODES.siteSettingsPage,
      PERMISSION_CODES.aboutSectionRead,
    ],
    [
      PERMISSION_CODES.systemSettingsMenu,
      PERMISSION_CODES.siteSettingsPage,
      PERMISSION_CODES.siteDomainSettingsRead,
    ],
  ),
  '/system-settings/user-permissions': requireAll(
    PERMISSION_CODES.systemSettingsMenu,
    PERMISSION_CODES.userPermissionsPage,
  ),
  '/system-settings/wechat-payment-settings': requireAll(
    PERMISSION_CODES.wechatPaymentSettingsPage,
    PERMISSION_CODES.wechatPaymentSettingsQuery,
  ),
  '/system-settings/user-management': requireAll(
    PERMISSION_CODES.systemSettingsMenu,
    PERMISSION_CODES.userManagementPage,
  ),
  '/system-settings/system-dictionary': requireAll(
    PERMISSION_CODES.systemSettingsMenu,
    PERMISSION_CODES.systemDictionaryPage,
  ),
  '/system-settings/integration-settings': integrationRequirement,
  '/system-settings/print-configuration': requireAll(
    PERMISSION_CODES.systemSettingsMenu,
    PERMISSION_CODES.printConfigurationPage,
  ),
  '/system-settings/audit-logs': requireAll(
    PERMISSION_CODES.systemSettingsMenu,
    PERMISSION_CODES.auditLogsPage,
  ),
  '/system-settings/tasks-queues': requireAll(
    PERMISSION_CODES.systemSettingsMenu,
    PERMISSION_CODES.tasksQueuesPage,
    PERMISSION_CODES.systemTaskQuery,
  ),
  '/system-settings/ai-management': requireAnyClause(
    [
      PERMISSION_CODES.systemSettingsMenu,
      PERMISSION_CODES.aiManagementMenu,
      PERMISSION_CODES.aiManagementConfigurationPage,
      PERMISSION_CODES.aiConfigurationRead,
    ],
    [
      PERMISSION_CODES.systemSettingsMenu,
      PERMISSION_CODES.aiManagementMenu,
      PERMISSION_CODES.aiManagementStatisticsPage,
      PERMISSION_CODES.aiStatisticsRead,
    ],
  ),
};

const routeTarget = (path: string): AuthorizedRedirectTarget => ({
  path,
  requirement: ROUTE_PERMISSION_REQUIREMENTS[path],
});

const memberTargets = [
  routeTarget('/member-center/member-list'),
  routeTarget('/member-center/member-certification'),
  routeTarget('/member-center/tag-management'),
  routeTarget('/member-center/audience/plans'),
  routeTarget('/member-center/member-relations'),
  routeTarget('/member-center/member-network'),
  routeTarget('/member-center/member-benefits'),
  routeTarget('/member-center/semiannual-confirmation'),
  routeTarget('/member-center/member-reports'),
];

const activityTargets = [
  routeTarget('/activity-center/activity-list'),
  routeTarget('/activity-center/frontend-operation'),
  routeTarget('/activity-center/activity-workflow'),
  routeTarget('/activity-center/process-templates'),
  routeTarget('/activity-center/registration-management'),
  routeTarget('/activity-center/check-in-management'),
  routeTarget('/activity-center/seating-management'),
  routeTarget('/activity-center/label-printing'),
  routeTarget('/activity-center/label-templates'),
  routeTarget('/activity-center/activity-notification'),
  routeTarget('/activity-center/activity-lottery'),
  routeTarget('/activity-center/activity-landing-page'),
  routeTarget('/activity-center/activity-summary'),
];

const financeTargets = [
  routeTarget('/finance-center/payment-management'),
  routeTarget('/finance-center/refund-management'),
  routeTarget('/finance-center/sponsorship-management'),
  routeTarget('/finance-center/material-management'),
  routeTarget('/finance-center/invoice-management'),
  routeTarget('/finance-center/financial-approval'),
  routeTarget('/finance-center/financial-reports'),
];

const notificationTargets = [
  routeTarget('/notification-center/notification-send'),
  routeTarget('/notification-center/notification-templates'),
  routeTarget('/notification-center/trigger-strategies'),
  routeTarget('/notification-center/send-records'),
];

const knowledgeTargets = [
  routeTarget('/knowledge-base/asset-management'),
  routeTarget('/knowledge-base/activity-materials'),
  routeTarget('/knowledge-base/template-library'),
  routeTarget('/knowledge-base/user-feedback'),
];

const taskTargets = [
  routeTarget('/task-approval/todo-center'),
  routeTarget('/task-approval/approval-workflow'),
  routeTarget('/task-approval/exception-tasks'),
];

const dataReportTargets = [
  routeTarget('/data-reports/operations-dashboard'),
  routeTarget('/data-reports/member-analysis'),
  routeTarget('/data-reports/activity-analysis'),
  routeTarget('/data-reports/registration-checkin-analysis'),
  routeTarget('/data-reports/finance-analysis'),
  routeTarget('/data-reports/notification-analysis'),
  routeTarget('/data-reports/lottery-analysis'),
];

const systemTargets = [
  routeTarget('/system-settings/organization-settings'),
  routeTarget('/system-settings/site-settings'),
  routeTarget('/system-settings/user-permissions'),
  routeTarget('/system-settings/user-management'),
  routeTarget('/system-settings/system-dictionary'),
  routeTarget('/system-settings/integration-settings'),
  routeTarget('/system-settings/print-configuration'),
  routeTarget('/system-settings/audit-logs'),
  routeTarget('/system-settings/tasks-queues'),
  routeTarget('/system-settings/ai-management'),
];

const withParentRequirement = (
  parentPath: string,
  targets: AuthorizedRedirectTarget[],
): AuthorizedRedirectTarget[] => {
  const parentRequirement = ROUTE_PERMISSION_REQUIREMENTS[parentPath];
  return targets.map((target) => ({
    ...target,
    requirement: combineRequirements(parentRequirement, target.requirement),
  }));
};

export const AUTHORIZED_REDIRECT_TARGETS: Readonly<
Record<string, readonly AuthorizedRedirectTarget[]>
> = {
  '/': [
    routeTarget('/workbench'),
    ...withParentRequirement('/member-center', memberTargets),
    ...withParentRequirement('/activity-center', activityTargets),
    ...withParentRequirement('/finance-center', financeTargets),
    ...withParentRequirement('/notification-center', notificationTargets),
    ...withParentRequirement('/knowledge-base', knowledgeTargets),
    ...withParentRequirement('/task-approval', taskTargets),
    ...withParentRequirement('/data-reports', dataReportTargets),
    ...withParentRequirement('/system-settings', systemTargets),
  ],
  '/member-center': memberTargets,
  '/activity-center': activityTargets,
  '/finance-center': financeTargets,
  '/notification-center': notificationTargets,
  '/knowledge-base': knowledgeTargets,
  '/task-approval': taskTargets,
  '/data-reports': dataReportTargets,
  '/system-settings': systemTargets,
};

export const satisfiesPermissionRequirement = (
  permissionCodes: readonly string[],
  requirement?: PermissionRequirement,
) => {
  if (!requirement?.length) {
    return false;
  }
  const permissionCodeSet = new Set(permissionCodes);
  return requirement.some((clause) =>
    clause.every((permissionCode) => permissionCodeSet.has(permissionCode)));
};

export const findAuthorizedRedirectTarget = (
  permissionCodes: readonly string[],
  entryPath?: string,
) =>
  (entryPath
    ? AUTHORIZED_REDIRECT_TARGETS[entryPath]?.find((target) =>
      satisfiesPermissionRequirement(permissionCodes, target.requirement))?.path
    : undefined);

export const canAccessRoutePath = (
  permissionCodes: readonly string[],
  routePath?: string,
) => {
  if (
    !routePath ||
    !satisfiesPermissionRequirement(
      permissionCodes,
      ROUTE_PERMISSION_REQUIREMENTS[routePath],
    )
  ) {
    return false;
  }

  return (
    !AUTHORIZED_REDIRECT_TARGETS[routePath] ||
    Boolean(findAuthorizedRedirectTarget(permissionCodes, routePath))
  );
};

export const canAccessAuthorizedEntryPath = (
  permissionCodes: readonly string[],
  entryPath?: string,
) => {
  if (!entryPath || !findAuthorizedRedirectTarget(permissionCodes, entryPath)) {
    return false;
  }
  return entryPath === '/' || canAccessRoutePath(permissionCodes, entryPath);
};
